import { useTranslation } from '@silospot/i18n/react';
import { CLAIM_STAGES, type ClaimStatus, type GoogleAdsClaimDossier } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, ExternalLink, FileText, Scale, ShieldCheck, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { Badge, Btn, Card, PageHeader } from '../../components/cld';
import { GoogleAdsNoLinkCard, GoogleAdsNoWatchedCard } from '../../components/cld/google-ads-empty';
import { api } from '../../lib/api';
import { useSiteAdAccounts } from '../../lib/use-site-ad-accounts';
import { useWatchedSet } from '../../lib/watched-audit';

const SUPPORT_URL = 'https://support.google.com/google-ads/gethelp';
const STATUS_TONE: Record<ClaimStatus, 'neutral' | 'suspect' | 'blue' | 'clean' | 'fraud'> = {
  open: 'neutral',
  prepared: 'blue',
  sent: 'suspect',
  credited: 'clean',
  refused: 'fraud',
};

export function GoogleAdsClaimsPage(): ReactElement {
  const { t } = useTranslation('pages');
  const nf = new Intl.NumberFormat('fr-FR');
  const money = (eur: number) => `${nf.format(Math.round(eur))} €`;

  const { projectId, connected, accounts, loading } = useSiteAdAccounts();
  const [picked, setPicked] = useState<string | null>(null);
  const cid = picked ?? accounts[0]?.id ?? null;
  const { watchedIds } = useWatchedSet(projectId, cid);
  const claimsQ = useQuery({
    queryKey: ['gads-claims', cid],
    queryFn: () => api.googleAdsClaims(cid as string),
    enabled: !!cid,
  });
  const [open, setOpen] = useState<GoogleAdsClaimDossier | null>(null);

  const data = claimsQ.data;
  // Only dossiers for monitored campaigns (the page reads the watched set).
  const dossiers = (data?.dossiers ?? []).filter(
    (d) => !watchedIds || watchedIds.has(d.campaignId),
  );
  // Estimated Google credits already received = invalid clicks × avg CPC.
  const googleCredits = dossiers.reduce(
    (s, d) => s + d.googleInvalidClicks * (d.clicks > 0 ? d.costEur / d.clicks : 0),
    0,
  );
  const suspectCost = data?.totals.contestableEstimateEur ?? 0;
  const contestable = Math.max(0, suspectCost);

  const header = (
    <PageHeader
      title={t('gadsc.title')}
      sub={t('gadsc.subtitle')}
      actions={
        connected && accounts.length > 0 ? (
          <div className="flex items-center gap-2">
            <select
              value={cid ?? ''}
              onChange={(e) => setPicked(e.target.value)}
              className="rounded-[9px] border border-border-strong bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-primary"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ? `${a.name} (${a.id})` : a.id}
                </option>
              ))}
            </select>
            <a
              href={SUPPORT_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[9px] border border-border-strong px-3 py-1.5 text-[13px] font-semibold text-text-2 hover:bg-bg"
            >
              {t('gadsc.openTicket')} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        ) : undefined
      }
    />
  );

  if (!connected) {
    return (
      <div>
        {header}
        <Card className="mx-auto max-w-md py-10 text-center">
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-accent-bg text-primary">
            <Scale className="h-6 w-6" />
          </span>
          <div className="text-[16px] font-bold text-text">{t('gadsp.notConnectedTitle')}</div>
          <p className="mx-auto mt-1 max-w-xs text-[13px] text-text-2">
            {t('gadsp.notConnectedBody')}
          </p>
        </Card>
      </div>
    );
  }

  if (connected && !loading && accounts.length === 0) {
    return (
      <div>
        {header}
        <GoogleAdsNoLinkCard projectId={projectId} />
      </div>
    );
  }

  if (connected && !loading && accounts.length > 0 && watchedIds && watchedIds.size === 0) {
    return (
      <div>
        {header}
        <GoogleAdsNoWatchedCard projectId={projectId} />
      </div>
    );
  }

  return (
    <div>
      {header}

      {/* Read-only doctrine banner */}
      <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-accent-border bg-accent-bg px-4 py-3 text-[13px] text-accent-strong">
        <ShieldCheck className="mt-0.5 h-[18px] w-[18px] shrink-0" />
        <span>{t('gadsc.banner')}</span>
      </div>

      {/* Head calculation */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <div className="text-[12px] text-text-3">{t('gadsc.suspectCost')}</div>
          <div className="mt-1 text-[26px] font-bold tracking-[-0.02em]">{money(suspectCost)}</div>
        </Card>
        <Card>
          <div className="text-[12px] text-text-3">{t('gadsc.googleCredits')}</div>
          <div className="mt-1 text-[26px] font-bold tracking-[-0.02em] text-text-2">
            − {money(googleCredits)}
          </div>
        </Card>
        <Card className="border-accent-border bg-accent-bg/40">
          <div className="text-[12px] text-text-3">{t('gadsc.contestable')}</div>
          <div className="mt-1 text-[26px] font-bold tracking-[-0.02em] text-[#b45309]">
            {money(contestable)}
          </div>
          <div className="mt-1 text-[11px] text-text-3">{t('gadsc.contestableDisclaimer')}</div>
        </Card>
      </div>

      {/* Pipeline */}
      <div className="mt-4 flex flex-wrap gap-2">
        {CLAIM_STAGES.map((st) => (
          <div
            key={st}
            className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-[12.5px]"
          >
            <Badge tone={STATUS_TONE[st]}>{data?.pipeline[st] ?? 0}</Badge>
            <span className="text-text-2">{t(`gadsc.status${cap(st)}`)}</span>
          </div>
        ))}
      </div>

      {data && !data.siloTrackerAvailable ? (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#f6e2c4] bg-[#fdf4e7] px-4 py-3 text-[12.5px] text-suspect-fg">
          <span>⚠</span>
          <span>{t('gadsc.caveat')}</span>
        </div>
      ) : null}

      {/* Dossiers */}
      <Card className="mt-4 p-0">
        {claimsQ.isLoading ? (
          <div className="h-40 animate-pulse bg-neutral-bg" />
        ) : dossiers.length === 0 ? (
          <p className="py-12 text-center text-[13px] text-text-3">{t('gadsc.emptyDossiers')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  {[
                    t('gadsc.colCampaign'),
                    t('gadsc.colInvalid'),
                    t('gadsc.colSuspect'),
                    t('gadsc.colContestable'),
                    t('gadsc.colStatus'),
                    '',
                  ].map((h, i) => (
                    <th
                      key={h || i}
                      className={`whitespace-nowrap border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase text-text-3 ${i >= 1 && i <= 3 ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dossiers.map((d) => (
                  <tr key={d.campaignId} className="hover:bg-[#fafbfc]">
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5">
                      <div className="font-semibold text-text">{d.name}</div>
                      <div className="text-[11px] text-text-3">{d.channelType}</div>
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono text-text-2">
                      {nf.format(d.googleInvalidClicks)}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono">
                      {nf.format(d.suspectBilledClicks)}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono text-[#b45309]">
                      {money(d.contestableEstimateEur)}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5">
                      <Badge tone={STATUS_TONE[d.status]}>
                        {t(`gadsc.status${cap(d.status)}`)}
                      </Badge>
                    </td>
                    <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => setOpen(d)}
                        className="text-[12.5px] font-semibold text-accent-strong hover:underline"
                      >
                        {t('gadsc.prepare')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-[#f6e2c4] bg-[#fdf4e7] px-[11px] py-[7px] text-[11.5px] leading-snug text-suspect-fg">
        {t('gadsc.estimatedNote')}
      </div>

      {open && cid ? (
        <DossierDrawer customerId={cid} dossier={open} onClose={() => setOpen(null)} />
      ) : null}
    </div>
  );
}

function DossierDrawer({
  customerId,
  dossier,
  onClose,
}: {
  customerId: string;
  dossier: GoogleAdsClaimDossier;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const nf = new Intl.NumberFormat('fr-FR');
  const [tab, setTab] = useState<'proofs' | 'summary' | 'tracking'>('proofs');
  const [caseNum, setCaseNum] = useState(dossier.googleCaseNumber);
  const [notes, setNotes] = useState(dossier.notes);

  const summary = `Demande de révision de clics invalides — Google Ads
Compte : ${customerId} · Campagne : ${dossier.name} (${dossier.channelType})
Période : 30 derniers jours

Clics facturés suspects détectés par Silospot : ${dossier.suspectBilledClicks} (estimation ~${Math.round(dossier.contestableEstimateEur)} €)
Clics invalides déjà filtrés par Google (contexte, non facturés) : ${dossier.googleInvalidClicks}

Preuves jointes : interactions gclid (IP pseudonymisées), score de risque, motif.
Montant = estimation Silospot, à valider par Google. Silospot ne soumet pas à votre place.`;

  const update = useMutation({
    mutationFn: (patch: { status?: ClaimStatus; googleCaseNumber?: string; notes?: string }) =>
      api.googleAdsUpdateClaim({
        customerId,
        campaignId: dossier.campaignId,
        period: dossier.period,
        estimatedCents: Math.round(dossier.contestableEstimateEur * 100),
        ...patch,
      }),
    onSuccess: () => {
      toast.success(t('gadsc.saved'));
      void qc.invalidateQueries({ queryKey: ['gads-claims'] });
    },
  });

  const exportProofs = (kind: 'csv' | 'json') => {
    const head = 'date,gclid,ip_masked,asn,device,duration_ms,score,reason';
    const body = dossier.proofs
      .map(
        (p) =>
          `${p.date},${p.gclid},${p.ipMasked},${p.asn},${p.device},${p.durationMs},${p.fraudScore},"${p.reason}"`,
      )
      .join('\n');
    const content = kind === 'json' ? JSON.stringify(dossier.proofs, null, 2) : `${head}\n${body}`;
    const blob = new Blob([content], {
      type: kind === 'json' ? 'application/json' : 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `silospot-claim-${dossier.campaignId}.${kind}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const TABS: { key: 'proofs' | 'summary' | 'tracking'; label: string }[] = [
    { key: 'proofs', label: t('gadsc.tabProofs') },
    { key: 'summary', label: t('gadsc.tabSummary') },
    { key: 'tracking', label: t('gadsc.tabTracking') },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <button
        type="button"
        aria-label="close"
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(15,21,32,0.42)]"
      />
      <div className="relative flex h-full w-[560px] max-w-full flex-col overflow-y-auto bg-surface shadow-[-12px_0_40px_rgba(16,24,40,0.18)]">
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <div className="text-[15px] font-bold text-text">{dossier.name}</div>
            <div className="text-[12px] text-text-3">
              {dossier.channelType} · {dossier.period}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="grid h-8 w-8 place-items-center rounded-md text-text-2 hover:bg-bg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1 border-b border-border px-4 pt-2">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              className={`border-b-2 px-3 py-2 text-[13px] font-semibold ${tab === tb.key ? 'border-primary text-primary' : 'border-transparent text-text-3 hover:text-text-2'}`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="flex-1 p-4">
          {tab === 'proofs' ? (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <Btn icon={Download} onClick={() => exportProofs('csv')}>
                  CSV
                </Btn>
                <Btn icon={Download} onClick={() => exportProofs('json')}>
                  JSON
                </Btn>
                <Btn icon={FileText} onClick={() => window.print()}>
                  PDF
                </Btn>
              </div>
              {dossier.proofs.length === 0 ? (
                <p className="rounded-lg border border-[#f6e2c4] bg-[#fdf4e7] px-3.5 py-3 text-[12.5px] text-suspect-fg">
                  {t('gadsc.emptyProofs')}
                </p>
              ) : (
                <table className="w-full border-collapse text-[12px]">
                  <thead>
                    <tr>
                      {[
                        t('gadsc.pDate'),
                        t('gadsc.pIp'),
                        t('gadsc.pScore'),
                        t('gadsc.pReason'),
                      ].map((h) => (
                        <th
                          key={h}
                          className="border-b border-border px-2 py-1.5 text-left text-[10.5px] uppercase text-text-3"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dossier.proofs.map((p) => (
                      <tr key={p.gclid}>
                        <td className="border-b border-[#f1f3f6] px-2 py-1.5">
                          {p.date.slice(0, 10)}
                        </td>
                        <td className="border-b border-[#f1f3f6] px-2 py-1.5 font-mono">
                          {p.ipMasked}
                        </td>
                        <td className="border-b border-[#f1f3f6] px-2 py-1.5">{p.fraudScore}</td>
                        <td className="border-b border-[#f1f3f6] px-2 py-1.5">{p.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="mt-3 text-[11.5px] text-text-3">
                {t('gadsc.colInvalid')} : <b>{nf.format(dossier.googleInvalidClicks)}</b>
              </div>
            </div>
          ) : tab === 'summary' ? (
            <div>
              <p className="mb-2 text-[12px] text-text-3">{t('gadsc.summaryHint')}</p>
              <textarea
                readOnly
                value={summary}
                className="h-56 w-full rounded-lg border border-border bg-bg p-3 font-mono text-[11.5px] text-text-2"
              />
              <div className="mt-3">
                <Btn
                  primary
                  icon={Copy}
                  onClick={() => {
                    void navigator.clipboard.writeText(summary);
                    toast.success(t('gadsc.copied'));
                  }}
                >
                  {t('gadsc.copySummary')}
                </Btn>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Field label={t('gadsc.colStatus')}>
                <Badge tone={STATUS_TONE[dossier.status]}>
                  {t(`gadsc.status${cap(dossier.status)}`)}
                </Badge>
              </Field>
              <Field label={t('gadsc.trCase')}>
                <input
                  value={caseNum}
                  onChange={(e) => setCaseNum(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label={t('gadsc.trNotes')}>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`${inputCls} h-20`}
                />
              </Field>
              <Field label={t('gadsc.trSentAt')}>
                <span className="text-text-2">
                  {dossier.sentAt ? dossier.sentAt.slice(0, 10) : '—'}
                </span>
              </Field>
              <div className="mt-1 flex flex-wrap gap-2">
                <Btn
                  onClick={() =>
                    update.mutate({ status: 'prepared', googleCaseNumber: caseNum, notes })
                  }
                >
                  {t('gadsc.prepare')}
                </Btn>
                <Btn
                  primary
                  onClick={() =>
                    update.mutate({ status: 'sent', googleCaseNumber: caseNum, notes })
                  }
                >
                  {t('gadsc.markSent')}
                </Btn>
                <Btn icon={FileText} onClick={() => window.print()}>
                  {t('gadsc.downloadReport')}
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls =
  'w-full rounded-[9px] border border-border-strong px-3 py-2 text-[13px] outline-none focus:border-primary';

function Field({ label, children }: { label: string; children: React.ReactNode }): ReactElement {
  return (
    <div>
      <div className="mb-1 text-[12px] font-semibold text-text-2">{label}</div>
      {children}
    </div>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
